/**
 * Dynamic Theme Hero — carousel + CSS variable theme engine.
 * Updates document-level custom properties so header/search/nav react smoothly.
 */
(function () {
  'use strict';

  var TRANSITION_EASE = 'ease-in-out';

  function parseConfig(root) {
    var id = root.getAttribute('data-section-id');
    var el = document.getElementById('dth-theme-data-' + id);
    if (!el) return null;
    try {
      return JSON.parse(el.textContent);
    } catch (e) {
      return null;
    }
  }

  function computeHeaderBg(slide) {
    var mode = slide.headerMode || 'solid';
    if (mode === 'transparent') {
      return { color: 'transparent', image: 'none' };
    }
    if (mode === 'glass') {
      return { color: hexToRgba(slide.headerBg || '#ffffff', 0.72), image: 'none', glass: true };
    }
    if (mode === 'gradient') {
      var start = slide.headerGradientStart || slide.headerBg || '#ffffff';
      var end = slide.headerGradientEnd || slide.headerBg || '#f5f5f5';
      return { color: 'transparent', image: 'linear-gradient(180deg, ' + start + ', ' + end + ')' };
    }
    return { color: slide.headerBg || '#ffffff', image: 'none' };
  }

  function computePageBg(slide) {
    var type = slide.bgType || 'solid';
    if (type === 'solid') return slide.bgSolid || '#ffffff';
    if (type === 'linear') {
      return 'linear-gradient(' + (slide.bgGradientAngle || 180) + 'deg, ' + (slide.bgGradientStart || '#fff') + ', ' + (slide.bgGradientEnd || '#f0f0f0') + ')';
    }
    if (type === 'radial') {
      return 'radial-gradient(circle at center, ' + (slide.bgGradientStart || '#fff') + ', ' + (slide.bgGradientEnd || '#f0f0f0') + ')';
    }
    return slide.bgSolid || '#ffffff';
  }

  function hexToRgba(hex, alpha) {
    if (!hex || hex.indexOf('rgb') === 0) return hex;
    var h = hex.replace('#', '');
    if (h.length === 3) h = h[0] + h[0] + h[1] + h[1] + h[2] + h[2];
    var n = parseInt(h, 16);
    var r = (n >> 16) & 255;
    var g = (n >> 8) & 255;
    var b = n & 255;
    return 'rgba(' + r + ',' + g + ',' + b + ',' + alpha + ')';
  }

  function applyTheme(slide, config, root) {
    if (!slide) return;
    var html = document.documentElement;
    var ms = config.transitionMs || 500;
    html.classList.add('dth-theme-active');
    html.style.setProperty('--dth-transition-ms', ms + 'ms');
    html.style.setProperty('--dth-transition-ease', TRANSITION_EASE);

    if (config.enablePageTheme) {
      html.classList.add('dth-page-theme');
    } else {
      html.classList.remove('dth-page-theme');
    }

    var header = computeHeaderBg(slide);
    var pageBg = computePageBg(slide);
    html.style.setProperty('--hero-background', pageBg);
    html.style.setProperty('--campaign-bg', pageBg);
    html.style.setProperty('--header-background', slide.headerMode === 'transparent' ? pageBg : header.color);
    html.style.setProperty('--header-text', slide.headerText || '#111111');
    html.style.setProperty('--accent-color', slide.accent || '#111111');
    html.style.setProperty('--dth-header-bg', header.color);
    html.style.setProperty('--dth-header-bg-image', header.image || 'none');
    html.classList.toggle('dth-header-glass', slide.headerMode === 'glass');
    html.classList.toggle('dth-header-transparent', slide.headerMode === 'transparent');

    html.style.setProperty('--dth-header-text', slide.headerText || '#111111');
    html.style.setProperty('--dth-nav-color', slide.navColor || '#111111');
    html.style.setProperty('--dth-nav-active', slide.navActive || '#000000');
    html.style.setProperty('--dth-nav-underline', slide.navUnderline || '#000000');
    html.style.setProperty('--dth-search-bg', slide.searchBg || '#f5f5f5');
    html.style.setProperty('--dth-search-border', slide.searchBorder || '#e5e5e5');
    html.style.setProperty('--dth-search-text', slide.searchText || '#111111');
    html.style.setProperty('--dth-search-placeholder', slide.searchPlaceholder || '#888888');
    html.style.setProperty('--dth-search-icon', slide.searchIcon || '#111111');
    html.style.setProperty('--dth-search-radius', (slide.searchRadius != null ? slide.searchRadius : 999) + 'px');
    html.style.setProperty('--dth-accent', slide.accent || '#111111');
    html.style.setProperty('--dth-button-font', slide.buttonFont || '#111111');
    html.style.setProperty('--dth-hero-font', slide.heroFont || '#111111');
    html.style.setProperty('--dth-hero-page-bg', pageBg);

    (function syncHeaderIconClass(textColor) {
      var value = String(textColor || '#111111').trim();
      var lum = 0;
      if (value.indexOf('rgb') === 0) {
        var parts = value.replace(/rgba?\(|\)|\s/g, '').split(',');
        if (parts.length >= 3) {
          lum =
            0.2126 * (parseFloat(parts[0]) / 255) +
            0.7152 * (parseFloat(parts[1]) / 255) +
            0.0722 * (parseFloat(parts[2]) / 255);
        }
      } else {
        var hex = value.replace('#', '');
        if (hex.length === 3) hex = hex.split('').map(function (c) { return c + c; }).join('');
        if (hex.length === 6) {
          lum =
            (0.2126 * parseInt(hex.substring(0, 2), 16) +
              0.7152 * parseInt(hex.substring(2, 4), 16) +
              0.0722 * parseInt(hex.substring(4, 6), 16)) /
            255;
        }
      }
      var light = lum > 0.55;
      html.classList.toggle('dch-header-icons-light', light);
      html.classList.toggle('dth-header-icons-light', light);
    })(slide.headerText || '#111111');

    if (root) {
      var canvas = root.querySelector('[data-dth-canvas]');
      if (canvas) {
        canvas.style.removeProperty('--dth-canvas-bg');
      }
    }

    var wrap = document.querySelector('.header-wrapper--skipp-nav-theme');
    if (wrap) {
      var mode = slide.headerMode || 'solid';
      var headerBg = slide.headerBg || '#ffffff';
      if (mode === 'gradient') {
        wrap.style.setProperty('--skipp-nav-grad-top', slide.headerGradientStart || headerBg);
        wrap.style.setProperty('--skipp-nav-grad-mid', slide.headerGradientStart || headerBg);
        wrap.style.setProperty('--skipp-nav-grad-bottom', slide.headerGradientEnd || headerBg);
      } else if (mode === 'solid' || mode === 'glass') {
        wrap.style.setProperty('--skipp-nav-grad-top', headerBg);
        wrap.style.setProperty('--skipp-nav-grad-mid', headerBg);
        wrap.style.setProperty('--skipp-nav-grad-bottom', headerBg);
      } else if (mode === 'transparent') {
        wrap.style.setProperty('--skipp-nav-grad-top', 'transparent');
        wrap.style.setProperty('--skipp-nav-grad-mid', 'transparent');
        wrap.style.setProperty('--skipp-nav-grad-bottom', 'transparent');
      }
    }
  }

  function preloadSlide(slide) {
    if (!slide) return;
    var url = slide.thumbDesktop || slide.thumbMobile;
    if (!url) return;
    var img = new Image();
    img.decoding = 'async';
    img.src = url;
  }

  function DynamicThemeHero(root) {
    this.root = root;
    this.config = parseConfig(root);
    this.slides = root.querySelectorAll('[data-dth-slide]');
    this.index = 0;
    this.timer = null;
    this.progressRaf = null;
    this.progressStart = 0;
    this.isPaused = false;

    if (!this.config || !this.config.slides || !this.config.slides.length) return;

    this.bind();
    this.goTo(0, true);
    if (this.config.autoplay && this.config.slides.length > 1) {
      this.startAutoplay();
    }
  }

  DynamicThemeHero.prototype.bind = function () {
    var self = this;
    var prev = this.root.querySelector('[data-dth-prev]');
    var next = this.root.querySelector('[data-dth-next]');
    if (prev) prev.addEventListener('click', function () { self.prev(); });
    if (next) next.addEventListener('click', function () { self.next(); });

    this.root.querySelectorAll('[data-dth-dot]').forEach(function (dot) {
      dot.addEventListener('click', function () {
        self.goTo(parseInt(dot.getAttribute('data-index'), 10));
      });
    });

    this.root.querySelectorAll('[data-dth-thumb]').forEach(function (thumb) {
      thumb.addEventListener('click', function () {
        self.goTo(parseInt(thumb.getAttribute('data-index'), 10));
      });
    });

    if (this.config.pauseOnHover) {
      this.root.addEventListener('mouseenter', function () {
        self.isPaused = true;
        self.stopAutoplay();
      });
      this.root.addEventListener('mouseleave', function () {
        self.isPaused = false;
        if (self.config.autoplay) self.startAutoplay();
      });
    }

    var viewport = this.root.querySelector('[data-dth-viewport]');
    if (viewport) {
      var touchStartX = 0;
      viewport.addEventListener('touchstart', function (e) {
        touchStartX = e.changedTouches[0].screenX;
      }, { passive: true });
      viewport.addEventListener('touchend', function (e) {
        var dx = e.changedTouches[0].screenX - touchStartX;
        if (Math.abs(dx) < 40) return;
        if (dx < 0) self.next();
        else self.prev();
      }, { passive: true });
    }
  };

  DynamicThemeHero.prototype.startAutoplay = function () {
    var self = this;
    this.stopAutoplay();
    this.progressStart = performance.now();
    this.animateProgress();
    this.timer = window.setInterval(function () {
      self.next();
    }, this.config.autoplayDelay || 5000);
  };

  DynamicThemeHero.prototype.stopAutoplay = function () {
    if (this.timer) {
      clearInterval(this.timer);
      this.timer = null;
    }
    if (this.progressRaf) {
      cancelAnimationFrame(this.progressRaf);
      this.progressRaf = null;
    }
    var bar = this.root.querySelector('[data-dth-progress-bar]');
    if (bar) bar.style.width = '0%';
  };

  DynamicThemeHero.prototype.animateProgress = function () {
    if (!this.config.showProgress) return;
    var self = this;
    var bar = this.root.querySelector('[data-dth-progress-bar]');
    if (!bar) return;
    var duration = this.config.autoplayDelay || 5000;

    function tick(now) {
      if (!self.timer) return;
      var elapsed = now - self.progressStart;
      var pct = Math.min(100, (elapsed / duration) * 100);
      bar.style.width = pct + '%';
      if (pct < 100) self.progressRaf = requestAnimationFrame(tick);
    }
    self.progressRaf = requestAnimationFrame(tick);
  };

  DynamicThemeHero.prototype.prev = function () {
    var count = this.config.slides.length;
    var nextIndex = this.index - 1;
    if (nextIndex < 0) {
      nextIndex = this.config.loop ? count - 1 : 0;
    }
    this.goTo(nextIndex);
  };

  DynamicThemeHero.prototype.next = function () {
    var count = this.config.slides.length;
    var nextIndex = this.index + 1;
    if (nextIndex >= count) {
      nextIndex = this.config.loop ? 0 : count - 1;
    }
    this.goTo(nextIndex);
  };

  DynamicThemeHero.prototype.goTo = function (index, instant) {
    var count = this.config.slides.length;
    if (index < 0 || index >= count) return;
    if (index === this.index && !instant) return;

    this.index = index;
    var theme = this.config.slides[index];

    for (var i = 0; i < this.slides.length; i++) {
      var active = i === index;
      this.slides[i].classList.toggle('is-active', active);
      this.slides[i].setAttribute('aria-hidden', active ? 'false' : 'true');
      var bg = this.slides[i].querySelector('[data-dth-bg]');
      if (bg) bg.classList.toggle('is-active', active);
    }

    this.root.querySelectorAll('[data-dth-dot]').forEach(function (dot) {
      var on = parseInt(dot.getAttribute('data-index'), 10) === index;
      dot.classList.toggle('is-active', on);
      dot.setAttribute('aria-selected', on ? 'true' : 'false');
    });

    this.root.querySelectorAll('[data-dth-thumb]').forEach(function (thumb) {
      var on = parseInt(thumb.getAttribute('data-index'), 10) === index;
      thumb.classList.toggle('is-active', on);
      thumb.setAttribute('aria-selected', on ? 'true' : 'false');
    });

    var cur = this.root.querySelector('[data-dth-current]');
    if (cur) cur.textContent = String(index + 1);

    applyTheme(theme, this.config, this.root);

    var nextIdx = (index + 1) % count;
    preloadSlide(this.config.slides[nextIdx]);

    if (!instant && this.config.autoplay && !this.isPaused) {
      this.startAutoplay();
    }
  };

  function initAll() {
    document.querySelectorAll('[data-dth-root]').forEach(function (root) {
      if (root.dataset.dthInit === '1') return;
      root.dataset.dthInit = '1';
      new DynamicThemeHero(root);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', function (e) {
    var root = e.target.querySelector('[data-dth-root]');
    if (root) {
      root.dataset.dthInit = '';
      new DynamicThemeHero(root);
    }
  });
})();
